// What a generated document was written from, what would change it now, the
// organisation's standing instructions for it, rewriting one section, and
// writing every applicable document in one run.
//
// The backend decides all of it (services/policyGeneration.ts, lib/documentProvenance.ts,
// services/documentInputs.ts); these components only show it and send the request.

import { useEffect, useState } from "react";
import { theme, card, inputStyle } from "../theme";
import { api, apiErrorMessage } from "../api/client";
import { fmtDate } from "../lib/format";
import { rewritePolicySection, type DocumentProgress, type GenerationStage } from "../lib/generatePolicy";

// ── A document being written ─────────────────────────────────────────────────

const STAGE_LABELS: Record<GenerationStage, string> = {
  preparing: "Gathering your organisation's records",
  writing: "Writing the document",
  checking: "Checking it against the brief",
  revising: "Revising what the check found",
  formatting: "Formatting the Word document",
  saving: "Saving the new version",
};
/** Where the bar sits at least, once a stage is reached. */
const STAGE_FLOOR: Record<GenerationStage, number> = { preparing: 0.03, writing: 0.08, checking: 0.8, revising: 0.82, formatting: 0.93, saving: 0.97 };
/** What a document takes when the worker has not reported its estimate yet. */
const FALLBACK_ESTIMATE_MS = 3 * 60 * 1000;
/** A job still queued after this long suggests a busy or stopped worker. */
const QUEUE_WARNING_MS = 60 * 1000;

const minutes = (ms: number) => {
  const m = Math.max(1, Math.round(ms / 60_000));
  return `${m} minute${m === 1 ? "" : "s"}`;
};
const clock = (ms: number) => {
  const s = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};

/**
 * The worker's own account of a document being written: the stage it is at, roughly how
 * long is left from how long this organisation's recent documents took, and a plain
 * warning when the job has not started or is running long. Replaces a fixed animation
 * that crept to 92% and stopped whatever was happening.
 */
export function GenerationProgressView({ startedAt, progress, queuedAt }: {
  startedAt: number;
  progress?: DocumentProgress;
  queuedAt?: number;
}) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const estimateMs = progress?.estimateMs ?? FALLBACK_ESTIMATE_MS;
  const begun = progress?.startedAt ?? startedAt;
  const elapsed = now - begun;
  const stage = progress?.stage;
  const waiting = !progress && queuedAt !== undefined && now - queuedAt > QUEUE_WARNING_MS;
  const fraction = stage ? Math.max(STAGE_FLOOR[stage], Math.min(0.95, elapsed / estimateMs)) : Math.min(0.05, elapsed / estimateMs);
  const remaining = estimateMs - elapsed;
  const shownStages = GENERATION_STAGE_ORDER.filter((s) => s !== "revising" || stage === "revising" || reached(stage, "formatting"));

  const timeLine = waiting
    ? `Waiting for the document writer to start (${clock(now - (queuedAt ?? now))}). It may be busy with other documents; if this lasts more than a few minutes it may be offline — try again later.`
    : remaining > 30_000
      ? `About ${minutes(remaining)} left — documents like this usually take about ${minutes(estimateMs)}.`
      : remaining > 0
        ? "Nearly done."
        : `Taking longer than usual (documents like this usually take about ${minutes(estimateMs)}) — still working.`;

  return (
    <div>
      <p style={{ fontSize: 12, color: theme.color.subtle, margin: "0 0 8px", lineHeight: 1.6 }}>
        {timeLine} You can leave this page: the document, or the reason it failed, will be here when you come back.
      </p>
      <div style={{ background: theme.color.border, borderRadius: 3, height: 6, overflow: "hidden", marginBottom: 8 }}>
        <div style={{ height: "100%", width: `${Math.round(fraction * 100)}%`, background: waiting ? theme.color.warning : theme.color.accent, transition: "width 1s linear" }} />
      </div>
      <ol style={{ listStyle: "none", margin: 0, padding: 0, fontSize: 11, lineHeight: 1.8 }}>
        {shownStages.map((s) => {
          const done = stage ? reached(stage, s) && s !== stage : false;
          const current = s === stage;
          return (
            <li key={s} style={{ color: current ? theme.color.heading : done ? theme.color.subtle : theme.color.faint, fontWeight: current ? 600 : 400 }}>
              {done ? "✓ " : current ? "→ " : "· "}
              {STAGE_LABELS[s]}
              {current && s === "writing" && progress?.pass && progress.pass > 1 ? ` — part ${progress.pass} of a long document` : ""}
            </li>
          );
        })}
      </ol>
      <div style={{ fontSize: 10, color: theme.color.faint, marginTop: 6, fontVariantNumeric: "tabular-nums" }}>Running for {clock(elapsed)}</div>
    </div>
  );
}

const GENERATION_STAGE_ORDER: GenerationStage[] = ["preparing", "writing", "checking", "revising", "formatting", "saving"];
function reached(stage: GenerationStage | undefined, target: GenerationStage): boolean {
  return !!stage && GENERATION_STAGE_ORDER.indexOf(stage) >= GENERATION_STAGE_ORDER.indexOf(target);
}

// ── Provenance ────────────────────────────────────────────────────────────────

export interface DocumentProvenance {
  v: 1;
  model: string;
  provider: string;
  passes: number;
  revised: boolean;
  qualityIssues: string[];
  sectionRewrite: string | null;
  standards: string[];
  laws: string[];
  legalDuties: number;
  existingDocuments: string[];
  connectorTopics: string[];
  playbook: string | null;
  regulatorGuidance: boolean;
  licensedCopy: boolean;
  learningProfile: boolean;
  instructions: boolean;
  /** Lessons learned put in front of this version (backend lib/policyShortfalls.ts); absent on older records. */
  shortfalls?: string[];
}

const CONNECTOR_TOPIC_LABELS: Record<string, string> = {
  identity: "identity and MFA",
  devices: "managed devices",
  information: "information classification",
  cloud: "cloud estate",
  network: "network",
  detection: "detection and monitoring",
  discovery: "where sensitive data was found",
};

const warningBox: React.CSSProperties = {
  fontSize: 12, color: theme.color.warningText, background: theme.color.warningBg,
  border: `1px solid ${theme.color.warning}`, borderRadius: 3, padding: "8px 12px", lineHeight: 1.6,
};

const smallButton: React.CSSProperties = {
  padding: "5px 12px", borderRadius: 3, fontSize: 11, fontWeight: 600, cursor: "pointer",
  border: `1px solid ${theme.color.accent}`, background: theme.color.accentBg, color: theme.color.accentText,
};

const disclosureSummary: React.CSSProperties = {
  fontSize: 12, fontWeight: 600, color: theme.color.muted, cursor: "pointer", userSelect: "none",
};

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", lineHeight: 1.6 }}>
      <span style={{ color: theme.color.faint, minWidth: 150 }}>{label}</span>
      <span style={{ color: theme.color.text, flex: 1, minWidth: 160 }}>{children}</span>
    </div>
  );
}

/** The quality check's remaining findings: shown open, never behind a disclosure. */
export function ProvenanceWarnings({ provenance }: { provenance: DocumentProvenance | null | undefined }) {
  if (!provenance) return null;
  return (
    <>
      {provenance.sectionRewrite && (
        <div style={{ fontSize: 11, color: theme.color.subtle, marginBottom: 8 }}>
          Section rewritten: <strong style={{ color: theme.color.muted }}>{provenance.sectionRewrite}</strong>
        </div>
      )}
      {provenance.qualityIssues.length > 0 && (
        <div style={{ ...warningBox, marginBottom: 10 }}>
          <div style={{ fontWeight: 600 }}>The quality check still found:</div>
          <ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>
            {provenance.qualityIssues.map((q, i) => <li key={i}>{q}</li>)}
          </ul>
          <div style={{ marginTop: 4 }}>Fix these when you review the document, or rewrite the section concerned below.</div>
        </div>
      )}
    </>
  );
}

/** "Written from": collapsed by default, hidden for documents written before provenance was kept. */
export function ProvenancePanel({ provenance }: { provenance: DocumentProvenance | null | undefined }) {
  if (!provenance) return null;
  const p = provenance;
  const used: string[] = [];
  if (p.regulatorGuidance) used.push("the regulators' published guidance");
  if (p.licensedCopy) used.push("your licensed copy of the standard");
  if (p.learningProfile) used.push("the results of earlier compliance checks");
  if (p.instructions) used.push("your instructions for this document");
  if (p.shortfalls?.length) used.push(`${p.shortfalls.length === 1 ? "one lesson learned" : `${p.shortfalls.length} lessons learned`} recorded against the previous version`);

  return (
    <details style={{ marginTop: 10, border: `1px solid ${theme.color.border}`, borderRadius: 3, padding: "8px 12px", background: theme.color.panel }}>
      <summary style={disclosureSummary}>Written from</summary>
      <div style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 8, fontSize: 12 }}>
        <Row label="Model">
          {p.model} · {p.passes} AI {p.passes === 1 ? "pass" : "passes"}
          {p.revised && <> · revised once by the quality check</>}
        </Row>
        {p.standards.length > 0 && <Row label="Standards">{p.standards.join(", ")}</Row>}
        {p.laws.length > 0 && (
          <Row label="Laws in scope">
            {p.laws.join(", ")}
            {p.legalDuties > 0 && <> · {p.legalDuties} legal {p.legalDuties === 1 ? "duty" : "duties"} applied</>}
          </Row>
        )}
        {p.laws.length === 0 && p.legalDuties > 0 && (
          <Row label="Legal duties">{p.legalDuties} legal {p.legalDuties === 1 ? "duty" : "duties"} applied</Row>
        )}
        {p.existingDocuments.length > 0 && <Row label="Your documents read">{p.existingDocuments.join(", ")}</Row>}
        {p.connectorTopics.length > 0 && (
          <Row label="Connected sources">{p.connectorTopics.map((t) => CONNECTOR_TOPIC_LABELS[t] ?? t).join(", ")}</Row>
        )}
        {p.playbook && <Row label="Playbook">{p.playbook}</Row>}
        {used.length > 0 && <Row label="Also used">{used.join(", ")}</Row>}
      </div>
    </details>
  );
}

// ── Inputs changed ────────────────────────────────────────────────────────────

/** The chip on a card whose inputs have moved since the document was written. */
export function InputsChangedChip({ reasons }: { reasons: string[] | undefined }) {
  if (!reasons || reasons.length === 0) return null;
  return (
    <span
      title={`A regeneration would now write this document differently:\n${reasons.map((r) => `• ${r}`).join("\n")}`}
      style={{
        fontSize: 10, fontWeight: 600, padding: "2px 7px", borderRadius: 4, whiteSpace: "nowrap", cursor: "help",
        color: theme.color.accentText, background: theme.color.accentBg, border: `1px solid ${theme.color.accent}`,
      }}
    >
      Inputs changed
    </span>
  );
}

/** The same reasons, spelt out above the open document. */
export function InputsChangedNote({ reasons }: { reasons: string[] | undefined }) {
  if (!reasons || reasons.length === 0) return null;
  return (
    <div style={{ fontSize: 11, color: theme.color.text, background: theme.color.accentBg, border: `1px solid ${theme.color.accent}`, borderRadius: 3, padding: "6px 10px", marginBottom: 10, lineHeight: 1.6 }}>
      <div style={{ fontWeight: 600, color: theme.color.accentText }}>Inputs changed since this version was written. Regenerate to take them in:</div>
      <ul style={{ margin: "2px 0 0", paddingLeft: 18 }}>
        {reasons.map((r, i) => <li key={i}>{r}</li>)}
      </ul>
    </div>
  );
}

// ── Instructions ──────────────────────────────────────────────────────────────

interface InstructionsState { text: string; updatedAt: string | null; maxLength: number }

/** The organisation's standing instructions for one document, given to every generation of it. */
export function TemplateInstructions({ templateId, canWrite }: { templateId: string; canWrite: boolean }) {
  const [open, setOpen] = useState(false);
  const [saved, setSaved] = useState<InstructionsState | null>(null);
  const [draft, setDraft] = useState("");
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);

  useEffect(() => {
    if (!open || saved) return;
    api.get<InstructionsState>(`/templates/${templateId}/instructions`)
      .then((d) => { setSaved(d); setDraft(d.text ?? ""); })
      .catch((e) => setLoadError(apiErrorMessage(e, "Your instructions could not be loaded.")));
  }, [open, saved, templateId]);

  async function save() {
    if (!saved) return;
    setSaving(true);
    setMessage(null);
    try {
      const d = await api.put<{ text: string; updatedAt: string | null }>(`/templates/${templateId}/instructions`, { text: draft.trim() });
      setSaved({ ...saved, text: d.text ?? "", updatedAt: d.updatedAt });
      setDraft(d.text ?? "");
      setMessage({ ok: true, text: d.text ? "Saved. The next generation will follow them." : "Instructions removed." });
    } catch (e) {
      setMessage({ ok: false, text: apiErrorMessage(e, "Your instructions could not be saved.") });
    } finally {
      setSaving(false);
    }
  }

  const max = saved?.maxLength ?? 0;
  const over = max > 0 && draft.length > max;
  const dirty = saved !== null && draft.trim() !== (saved.text ?? "");

  return (
    <details
      onToggle={(e) => setOpen((e.target as HTMLDetailsElement).open)}
      style={{ marginTop: 12, border: `1px solid ${theme.color.border}`, borderRadius: 3, padding: "8px 12px", background: theme.color.panel }}
    >
      <summary style={disclosureSummary}>
        Your instructions for this document
        {saved?.text ? <span style={{ fontWeight: 400, color: theme.color.faint }}> · set</span> : null}
      </summary>
      <div style={{ fontSize: 11, color: theme.color.subtle, margin: "6px 0 8px", lineHeight: 1.6 }}>
        Given to every generation of this document, for example who approves it or a rule your organisation already follows.
      </div>
      {loadError && <div style={{ ...warningBox, fontSize: 11 }}>{loadError}</div>}
      {!loadError && !saved && open && <div style={{ fontSize: 11, color: theme.color.faint }}>Loading…</div>}
      {saved && (
        <>
          <textarea
            value={draft}
            onChange={(e) => { setDraft(e.target.value); setMessage(null); }}
            readOnly={!canWrite}
            rows={5}
            maxLength={max > 0 ? max : undefined}
            placeholder={canWrite ? "For example: the Head of IT approves this policy; we do not allow personal devices." : "No instructions have been set."}
            style={{ ...inputStyle, fontSize: 12, resize: "vertical" }}
          />
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginTop: 6 }}>
            <span style={{ fontSize: 11, color: over ? theme.color.dangerText : theme.color.faint, fontVariantNumeric: "tabular-nums" }}>
              {draft.length.toLocaleString("en-GB")} / {max.toLocaleString("en-GB")}
            </span>
            {saved.updatedAt && <span style={{ fontSize: 11, color: theme.color.faint }}>Last updated {fmtDate(saved.updatedAt)}</span>}
            {canWrite && (
              <button onClick={save} disabled={saving || over || !dirty} style={{ ...smallButton, marginLeft: "auto", opacity: saving || over || !dirty ? 0.5 : 1, cursor: saving || over || !dirty ? "default" : "pointer" }}>
                {saving ? "Saving…" : "Save"}
              </button>
            )}
          </div>
          {message && (
            <div style={{ fontSize: 11, marginTop: 6, color: message.ok ? theme.color.successText : theme.color.dangerText }}>{message.text}</div>
          )}
        </>
      )}
    </details>
  );
}

// ── Rewrite a section ─────────────────────────────────────────────────────────

/** The document's level-2 headings, exactly as written, without the leading "## ". */
export function levelTwoHeadings(content: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const line of content.split(/\r?\n/)) {
    if (!line.startsWith("## ")) continue;
    const h = line.slice(3).trim();
    if (h && !seen.has(h)) { seen.add(h); out.push(h); }
  }
  return out;
}

const FEEDBACK_MAX = 2000;

export function SectionRewriteForm<D>({ templateId, content, onRewritten }: {
  templateId: string;
  content: string;
  onRewritten: (result: { document: D; content: string }) => void;
}) {
  const headings = levelTwoHeadings(content);
  const [heading, setHeading] = useState("");
  const [feedback, setFeedback] = useState("");
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (headings.length === 0) return null;
  const chosen = headings.includes(heading) ? heading : "";
  const ready = !!chosen && feedback.trim().length > 0 && feedback.length <= FEEDBACK_MAX && !running;

  async function submit() {
    if (!ready) return;
    setRunning(true);
    setError(null);
    try {
      const result = await rewritePolicySection<D>(templateId, chosen, feedback.trim());
      setFeedback("");
      setHeading("");
      onRewritten(result);
    } catch (e) {
      setError(e instanceof Error ? e.message : "The section could not be rewritten. Please try again.");
    } finally {
      setRunning(false);
    }
  }

  return (
    <details style={{ marginTop: 10, border: `1px solid ${theme.color.border}`, borderRadius: 3, padding: "8px 12px", background: theme.color.panel }}>
      <summary style={disclosureSummary}>Rewrite a section</summary>
      <div style={{ fontSize: 11, color: theme.color.subtle, margin: "6px 0 8px", lineHeight: 1.6 }}>
        Say what is wrong with one section; only that section is rewritten and saved as a new version.
      </div>
      <select
        value={chosen}
        onChange={(e) => setHeading(e.target.value)}
        disabled={running}
        style={{ ...inputStyle, fontSize: 12, marginBottom: 8 }}
      >
        <option value="">Choose a section…</option>
        {headings.map((h) => <option key={h} value={h}>{h}</option>)}
      </select>
      <textarea
        value={feedback}
        onChange={(e) => setFeedback(e.target.value)}
        disabled={running}
        rows={4}
        maxLength={FEEDBACK_MAX}
        placeholder="For example: we review access quarterly, not annually; name the IT Manager as owner."
        style={{ ...inputStyle, fontSize: 12, resize: "vertical" }}
      />
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 6, flexWrap: "wrap" }}>
        <span style={{ fontSize: 11, color: theme.color.faint, fontVariantNumeric: "tabular-nums" }}>
          {feedback.length.toLocaleString("en-GB")} / {FEEDBACK_MAX.toLocaleString("en-GB")}
        </span>
        {running && <span style={{ fontSize: 11, color: theme.color.subtle }}>Rewriting the section — this usually takes a minute or two…</span>}
        <button onClick={submit} disabled={!ready} style={{ ...smallButton, marginLeft: "auto", opacity: ready ? 1 : 0.5, cursor: ready ? "pointer" : "default" }}>
          {running ? "Rewriting…" : "Rewrite section"}
        </button>
      </div>
      {error && (
        <div style={{ fontSize: 12, color: theme.color.dangerText, background: theme.color.dangerBg, border: `1px solid ${theme.color.danger}`, borderRadius: 3, padding: "6px 10px", marginTop: 8 }}>
          {error}
        </div>
      )}
    </details>
  );
}

// ── Generate every applicable document ────────────────────────────────────────

export interface SetRunView {
  status: "running" | "done" | "error";
  done: number;
  total: number;
  currentTitle?: string | null;
  results?: { templateId: string; title: string; ok: boolean; versionNumber?: number; error?: string }[];
  error?: string;
}

export function GenerateSetPanel({ count, run, onStart, onDismiss }: {
  count: number;
  run: SetRunView | null;
  onStart: () => void;
  onDismiss: () => void;
}) {
  const busy = run?.status === "running";
  const failures = run?.results?.filter((r) => !r.ok) ?? [];
  const successes = run?.results?.filter((r) => r.ok) ?? [];

  return (
    <div style={{ ...card, marginBottom: 16, padding: "12px 14px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 220 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: theme.color.heading }}>Generate all applicable documents</div>
          <div style={{ fontSize: 12, color: theme.color.subtle, lineHeight: 1.6 }}>
            Writes the {count} {count === 1 ? "document" : "documents"} that apply to your organisation, one after another. Each one uses AI credit. You can leave this page while it runs.
          </div>
        </div>
        <button
          onClick={onStart}
          disabled={busy || count === 0}
          style={{
            padding: "8px 16px", borderRadius: 3, fontSize: 12, fontWeight: 700,
            border: `1px solid ${theme.color.accent}`, background: theme.color.accentBg, color: theme.color.accentText,
            cursor: busy || count === 0 ? "default" : "pointer", opacity: busy || count === 0 ? 0.5 : 1,
          }}
        >
          {busy ? "Writing…" : `Generate ${count} ${count === 1 ? "document" : "documents"}`}
        </button>
      </div>

      {run?.status === "running" && (
        <div style={{ marginTop: 10 }}>
          <div style={{ fontSize: 12, color: theme.color.text, marginBottom: 6 }}>
            {run.total > 0
              ? <>Writing {Math.min(run.done + 1, run.total)} of {run.total}{run.currentTitle ? <> — {run.currentTitle}</> : null}</>
              : "Waiting for the worker to start…"}
          </div>
          <div style={{ background: theme.color.border, borderRadius: 3, height: 6, overflow: "hidden" }}>
            <div style={{ height: "100%", background: theme.color.accent, width: `${run.total > 0 ? Math.round((run.done / run.total) * 100) : 0}%`, transition: "width 0.4s" }} />
          </div>
        </div>
      )}

      {run?.status === "error" && (
        <div style={{ fontSize: 12, color: theme.color.dangerText, background: theme.color.dangerBg, border: `1px solid ${theme.color.danger}`, borderRadius: 3, padding: "8px 12px", marginTop: 10 }}>
          {run.error}
          <button onClick={onDismiss} style={{ marginLeft: 10, background: "none", border: "none", color: theme.color.dangerText, cursor: "pointer", fontSize: 11, textDecoration: "underline" }}>Dismiss</button>
        </div>
      )}

      {run?.status === "done" && (
        <div style={{ marginTop: 10, fontSize: 12, lineHeight: 1.6 }}>
          <div style={{ color: theme.color.successText }}>
            {successes.length} of {run.results?.length ?? 0} {(run.results?.length ?? 0) === 1 ? "document" : "documents"} written.
            {successes.length > 0 && " Review each one before you adopt it."}
          </div>
          {failures.length > 0 && (
            <div style={{ ...warningBox, marginTop: 6 }}>
              <div style={{ fontWeight: 600 }}>{failures.length} could not be written:</div>
              <ul style={{ margin: "2px 0 0", paddingLeft: 18 }}>
                {failures.map((f) => <li key={f.templateId}><strong>{f.title}</strong>{f.error ? ` — ${f.error}` : ""}</li>)}
              </ul>
            </div>
          )}
          <button onClick={onDismiss} style={{ marginTop: 6, background: "none", border: "none", padding: 0, color: theme.color.muted, cursor: "pointer", fontSize: 11, textDecoration: "underline" }}>Dismiss</button>
        </div>
      )}
    </div>
  );
}
